import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Subscribe from "@/pages/Subscribe";
import BillingSuccess from "@/pages/BillingSuccess";
import ChangePassword from "@/pages/ChangePassword";

import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import MasterProjects from "@/pages/MasterProjects";
import Listings from "@/pages/Listings";
import ManualListing from "@/pages/ManualListing";
import MyRentals from "@/pages/MyRentals";
import RentalDetail from "@/pages/RentalDetail";
import LocalityLanding from "@/pages/LocalityLanding";
import DeveloperLanding from "@/pages/DeveloperLanding";
import Leads from "@/pages/Leads";
import Calculator from "@/pages/Calculator";
import AgentMicrosite from "@/pages/AgentMicrosite";
import ProjectDetail from "@/pages/ProjectDetail";
import Analytics from "@/pages/Analytics";
import LeadReceipts from "@/pages/LeadReceipts";
import About from "@/pages/About";
import Browse from "@/pages/Browse";
import PublicBuy from "@/pages/PublicBuy";
import MarketInsights from "@/pages/MarketInsights";
import Financials from "@/pages/Financials";
import Rent from "@/pages/Rent";
import Sell from "@/pages/Sell";
import Branding from "@/pages/Branding";
import Terms from "@/pages/Terms";

import AdminDashboard from "@/pages/AdminDashboard";
import AdminAgents from "@/pages/AdminAgents";
import AdminLedger from "@/pages/AdminLedger";
import AdminUsers from "@/pages/AdminUsers";
import AdminLeads from "@/pages/AdminLeads";
import AdminBranding from "@/pages/AdminBranding";
import AdminContact from "@/pages/AdminContact";
import AdminCompliance from "@/pages/AdminCompliance";
import AdminBillingUPI from "@/pages/AdminBillingUPI";
import AdminIngestProject from "@/pages/AdminIngestProject";
import AdminBuilderIngest from "@/pages/AdminBuilderIngest";
import ContactUs from "@/pages/ContactUs";
import MarketingStudio from "@/pages/MarketingStudio";

import AgentLayout from "@/components/AgentLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import BackButton from "@/components/BackButton";
import InPropAIChat from "@/components/InPropAIChat";

function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/billing/success" element={<BillingSuccess />} />

      {/* Subscribe — needs auth but NOT subscription/onboarded */}
      <Route path="/subscribe" element={
        <ProtectedRoute requireSubscription={false} requireOnboarded={false} role="agent"><Subscribe /></ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute requireOnboarded={false} role="agent"><Onboarding /></ProtectedRoute>
      } />

      {/* Agent dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute role="agent"><Dashboard /></ProtectedRoute>} />
      <Route path="/dashboard/projects" element={<ProtectedRoute role="agent"><MasterProjects /></ProtectedRoute>} />
      <Route path="/dashboard/listings" element={<ProtectedRoute role={["agent", "owner"]} requireSubscription={false} requireOnboarded={false}><Listings /></ProtectedRoute>} />
      <Route path="/dashboard/listings/create" element={<ProtectedRoute role="agent"><ManualListing /></ProtectedRoute>} />
      <Route path="/dashboard/rentals" element={<ProtectedRoute role="agent"><MyRentals /></ProtectedRoute>} />
      <Route path="/dashboard/leads" element={<ProtectedRoute role="agent"><Leads /></ProtectedRoute>} />
      <Route path="/dashboard/calculator" element={<ProtectedRoute role="agent"><Calculator /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute role="agent"><Analytics /></ProtectedRoute>} />
      <Route path="/dashboard/lead-receipts" element={<ProtectedRoute role="agent"><LeadReceipts /></ProtectedRoute>} />
      <Route path="/dashboard/branding" element={<ProtectedRoute role="agent"><Branding /></ProtectedRoute>} />
      <Route path="/dashboard/marketing" element={<ProtectedRoute role="agent"><MarketingStudio /></ProtectedRoute>} />
      <Route path="/dashboard/change-password" element={<ProtectedRoute role="agent"><ChangePassword /></ProtectedRoute>} />

      {/* Owner / admin */}
      <Route path="/admin" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/agents" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminAgents /></ProtectedRoute>} />
      <Route path="/admin/ledger" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminLedger /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/listings/create" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><ManualListing /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/branding" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminBranding /></ProtectedRoute>} />
      <Route path="/admin/contact" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminContact /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminCompliance /></ProtectedRoute>} />
      <Route path="/admin/billing-upi" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminBillingUPI /></ProtectedRoute>} />
      <Route path="/admin/ingest-project" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminIngestProject /></ProtectedRoute>} />
      <Route path="/admin/builder-ingest" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><AdminBuilderIngest /></ProtectedRoute>} />
      <Route path="/admin/marketing" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><MarketingStudio /></ProtectedRoute>} />
      <Route path="/admin/change-password" element={<ProtectedRoute role="owner" requireSubscription={false} requireOnboarded={false}><ChangePassword /></ProtectedRoute>} />

      {/* Agent micro-site (always public, no auth) */}
      <Route path="/agent/:slug" element={<AgentLayout />}>
        <Route index element={<AgentMicrosite />} />
        <Route path="about" element={<About />} />
        <Route path="buy" element={<Browse mode="buy" />} />
        <Route path="new-launches" element={<Browse mode="new-launches" />} />
        <Route path="rent" element={<Rent />} />
        <Route path="rental/:id" element={<RentalDetail />} />
        <Route path="sell" element={<Sell />} />
        <Route path="market-insights" element={<MarketInsights />} />
        <Route path="financials" element={<Financials />} />
        <Route path="project/:id" element={<ProjectDetail />} />
      </Route>

      {/* Public top-level static pages */}
      <Route path="/project/:id" element={<ProjectDetail />} />
      <Route path="/about" element={<About />} />
      <Route path="/buy" element={<PublicBuy />} />
      <Route path="/listings/search" element={<Navigate to="/buy" replace />} />
      <Route path="/new-launches" element={<Browse mode="new-launches" />} />
      <Route path="/rent" element={<Rent />} />
      <Route path="/rental/:id" element={<RentalDetail />} />
      <Route path="/pune/:locality" element={<LocalityLanding />} />
      <Route path="/pune/:locality/:bhk" element={<LocalityLanding />} />
      <Route path="/pune/:locality/:bhk/:status" element={<LocalityLanding />} />
      <Route path="/developer/:slug" element={<DeveloperLanding />} />
      <Route path="/sell" element={<Sell />} />
      <Route path="/market-insights" element={<MarketInsights />} />
      <Route path="/financials" element={<Financials />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<ContactUs />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <BackButton />
        <AppRouter />
        <WhatsAppFAB />
        <InPropAIChat />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
